import React, { useEffect, useContext } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import Title from '../components/Title';
import useGtmEventTracking from '../components/useGtmEventTracking';
import UserContext from '../contexts/UserContext';
import { useFlow } from '../utilities/useFlow';
import { axiosKK } from '../services/networkRequest';

interface LandingProps extends RouteComponentProps {}

function LandingComponent({ history }: LandingProps): JSX.Element {
  const gtmEventTracking = useGtmEventTracking();
  const flow = useFlow();
  const isKeepr = flow === 'Keepr';
  const isStAnnes = flow === 'St Annes';

  useEffect(() => {
    gtmEventTracking({ event: 'homepage loaded', value: true });
  }, [gtmEventTracking]);

  const { user } = useContext(UserContext);

  const handleLogout = () => {
    localStorage.removeItem('user');
    delete axiosKK.defaults.headers.common['Authorization'];
    window.location.assign('/');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const eyebrow = isStAnnes
    ? "St Anne's College Partner Service"
    : isKeepr
      ? 'Storage · Shipping · Removals'
      : 'Student Storage & Shipping';

  const lead = isStAnnes
    ? "St Anne's College partner service — expert storage and shipping for Oxford students."
    : `${flow} are experts at storing and shipping boxes all across the UK.`;

  const helmetTitle = isStAnnes
    ? `Student Storage & Shipping | St Anne's College, Oxford`
    : `Student Storage & Removals | ${flow}`;
  const helmetDescription = isStAnnes
    ? `Student storage and shipping service for St Anne's College, Oxford. Door-to-door box collection, storage and delivery across the UK.`
    : `The #1 Student Storage and Shipping service in the UK. Door-to-door box storage and removals from ${flow}.`;

  return (
    <div className="landing">
      <Helmet>
        <title>{helmetTitle}</title>
        <meta name="description" content={helmetDescription} />
        <meta property="og:title" content={helmetTitle} />
        <meta property="og:description" content={helmetDescription} />
        <meta property="og:url" content="https://book.kitkeeper.co.uk/" />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="landing__hero">
        <div className="landing__hero-content">
          <p className="landing__eyebrow">{eyebrow}</p>
          {user ? (
            <Title>{`Welcome back, ${user.firstName}`}</Title>
          ) : (
            <Title>Let's get you moving</Title>
          )}
          <p className="landing__lead">{lead}</p>
        </div>

        <div className="landing__hero-rail">
          {user ? (
            <div className="landing-auth-pill landing-auth-pill--user">
              <Link to="/dashboard" className="landing-auth-pill__primary">
                Dashboard
                <ArrowRightIcon aria-hidden="true" />
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="landing-auth-pill__ghost"
              >
                Log out
              </button>
            </div>
          ) : (
            <div className="landing-auth-pill landing-auth-pill--guest">
              <Link to="/login" className="landing-auth-pill__primary">
                Log In
              </Link>
              <Link to="/register" className="landing-auth-pill__ghost">
                Register
              </Link>
            </div>
          )}
        </div>
      </section>

      <div className="landing__tiles">
        <LandingTile
          to="/storage/boxes"
          icon="/img/box-icon-large.png"
          title="Store Boxes"
          description="We provide an all-inclusive service including boxes, packing materials, collection, storage and delivery (one month minimum storage charge)."
          ctaLabel="Book Storage"
          onClick={() =>
            gtmEventTracking({ event: 'booking_step_option_storage' })
          }
        />
        <LandingTile
          to="/shipping/boxes"
          icon="/img/ship-icon.png"
          title="Ship Boxes"
          description="Our shipping service includes empty boxes, packing materials, collection and delivery of your items to anywhere in the UK."
          ctaLabel="Ship Boxes"
          onClick={() =>
            gtmEventTracking({ event: 'booking_step_option_ship' })
          }
        />
        {isKeepr && (
          <LandingTile
            to="/removals"
            icon="/img/truck.png"
            title="Removals"
            description="Our professional removals service includes collection, transportation and delivery of your furniture and belongings."
            ctaLabel="Book Removal"
            onClick={() =>
              gtmEventTracking({ event: 'booking_step_option_removals' })
            }
          />
        )}
      </div>
    </div>
  );
}

interface LandingTileProps {
  to: string;
  icon: string;
  title: string;
  description: string;
  ctaLabel: string;
  onClick: () => void;
}

function LandingTile({
  to,
  icon,
  title,
  description,
  ctaLabel,
  onClick,
}: LandingTileProps) {
  return (
    <Link to={to} onClick={onClick} className="landing-tile">
      <div className="landing-tile__icon">
        <img src={icon} alt="" />
      </div>
      <div className="landing-tile__body">
        <h3 className="landing-tile__title">{title}</h3>
        <p className="landing-tile__lead">{description}</p>
      </div>
      <span className="landing-tile__cta">
        {ctaLabel}
        <ArrowRightIcon aria-hidden="true" />
      </span>
    </Link>
  );
}

export default withRouter(LandingComponent);
